import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment, { Moment } from 'moment';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { LooseObject } from '../../../objects/loose-object';
import { environment } from '../../../../environments/environment';
import { CalendarPagination, CalendarPageDirection } from '../../calendar/full-calendar/classes/calendar-pagination';
import { DateFilter, Day } from '../classes/shared-types';
import { BrowserService } from '../../../services/browser/browser.service';
import { ClientStoreService, UserAccessLevel } from '../../../services/client-store.service';
import { TimesheetInterface } from '../classes/timesheet';
import { Client } from '../../../objects/client';
import { get } from 'lodash-es';
import { LocalStorageService } from '../../../services/local-storage.service';
import momentTimezone from 'moment-timezone';

const kBaseAPI = environment.url;

@Injectable()
export class TimesheetsService {

  /**
   * Pagination for the calendar view.
   *
   * @type {CalendarPagination}
   */
  objPagination: CalendarPagination = new CalendarPagination();

  /**
   * Resources Size Per Page
   *
   * @type {number}
   */
  numResourcesSizePerPage: number = 25;

  /**
   * Current selected date
   *
   * @type {moment.Moment}
   */
  objSelectedDate: moment.Moment;

  /**
   * The number of the week with respect to the year.
   *
   * @type {number}
   */
  numWeekNumber: number;

  /**
   * Flag that checks if buttons should be disabled
   *
   * @type {boolean}
   */
  bDisableButtons: boolean = false;

  /**
   * Displays the current selected month and year.
   * Eg. "January 2022"
   *
   * @type {string}
   */
  strMonthYearString: string;

  /**
   * Used for looping through days of the week
   *
   * @type {Day[]}
   */
  arDays: Day[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  /**
   * Contains the day numbers in the table header
   * ex: 'mon': 24
   *
   * @type {LooseObject}
   */
  objDayNumbers: LooseObject = {};

  objDateFilter: DateFilter|null = {
    from: '',
    to: ''
  };

  /**
   * Checks if the data in the table has loaded.
   *
   * @type {boolean}
   */
  bIsLoaded: boolean = false;

  /**
   * Subject for reloading the timesheets table.
   *
   * @type {Subject<void>}
   */
  tableUpdater: Subject<void> = new Subject<void>();

  /**
   * Observable for tableUpdater.
   *
   * @type {Observable<void>}
   */
  tableUpdate$: Observable<void> = this.tableUpdater.asObservable();

  /**
   * The client the user belongs to.
   *
   * @type {Client}
   */
  private _activeClient: Client;

  /**
   * Displays the week range with sample format:
   * "January 12-18 2024"
   * If end of the week falls on the next month, the format is:
   * "February 26-March 3 2024"
   *
   * @returns {string}
   */
  get weekRangeDisplay(): string {
    if (this.objSelectedDate) {
      const weekStart: moment.Moment = this.objSelectedDate.clone().startOf('isoWeek');
      const weekEnd: moment.Moment = this.objSelectedDate.clone().endOf('isoWeek');
      const strWeekEndFormat: string = weekStart.format('MM') === weekEnd.format('MM')
        ? 'D YYYY'
        : 'MMMM D YYYY';

      return `${weekStart.format('MMMM D')}-${weekEnd.format(strWeekEndFormat)}`;
    }

    return '';
  }

  get dateFilterLocalized(): DateFilter {
    const strTimezone = get(this._activeClient, 'config.timezone_name', momentTimezone.tz.guess());

    const date = momentTimezone.tz(
      this.objSelectedDate.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS),
      strTimezone
    );

    const startOfWeek = date.clone().startOf('isoWeek');

    const endOfWeek = date.clone().endOf('isoWeek');

    return {
      from: startOfWeek.clone().tz('UTC').format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS),
      to: endOfWeek.clone().tz('UTC').format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS),
    };
  }

  /**
   * Start range of the shown entries.
   *
   * @returns {number}
   */
  get numShowStart(): number {
    return (this.objPagination.current_page * this.numResourcesSizePerPage) + 1;
  }

  /**
   * End range of the shown entries.
   *
   * @returns {number}
   */
  get numShowEnd(): number {
    let page = ((this.objPagination.current_page * this.numResourcesSizePerPage) + this.numResourcesSizePerPage);
    return page > this.objPagination.total ? this.objPagination.total : page;
  }

  /**
   * Checks if the current user is not an admin and has no roles.
   *
   * @returns {number}
   */
  get isNotAdminAndHasNoRoles(): boolean {
    return this._activeClient.level !== UserAccessLevel.admin &&
      (!this._activeClient.roles || this._activeClient.roles.length === 0);
  }

  constructor(
    protected http: HttpClient,
    private browserService: BrowserService,
    private clientStoreService: ClientStoreService,
    private localStorageService: LocalStorageService,
  ) {
    this._activeClient = this.clientStoreService.getActiveClient();
    this.numResourcesSizePerPage = get(this._activeClient, 'config.listing_records_per_page', 25);
  }

  /**
   * Sets the current page to access.
   *
   * @param {CalendarPageDirection} strDirection
   *
   * @returns {void}
   */
  setPage(strDirection: CalendarPageDirection): void{
    switch (strDirection) {
      case 'first':
        this.objPagination.current_page = 0;
        break;
      case 'next':
        this.objPagination.current_page++;
        break;
      case 'prev':
        this.objPagination.current_page--;
        break;
      default:
        this.objPagination.current_page = Math.ceil(this.objPagination.total / this.numResourcesSizePerPage) - 1;
    }
  }

  updatePageDates(): void {
    this.objDayNumbers = {};
    this.numWeekNumber = this.objSelectedDate ? this.objSelectedDate.isoWeek() : 0;
    this.strMonthYearString = this.objSelectedDate ? this.objSelectedDate.format('MMMM YYYY') : '';
    this.setDateFilter();

    if (this.objSelectedDate) {
      const objDateClone = this.objSelectedDate.clone().startOf('isoWeek');
      this.arDays.forEach(strDay => {
        this.objDayNumbers[strDay] = objDateClone.format('D');
        objDateClone.add(1, 'day');
      });
    }
  }

  setDateFilter(): void {
    if (this.objSelectedDate) {
      this.objDateFilter = {
        from: this.objSelectedDate.startOf('isoWeek').format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS),
        to: this.objSelectedDate.endOf('isoWeek').format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS)
      };
    } else {
      this.objDateFilter = null;
    }
  }

  /**
   * When the date changes.
   *
   * @param {any} objEvent
   *
   * @returns {void}
   */
  onGoToDateChange(objEvent: any): void {
    this.objSelectedDate = moment(objEvent.value._d);
    this.updateTable();
  }

  updateTable(): void {
    this.updatePageDates();
    this.tableUpdater.next();
  }

  goToPrevWeek(): void {
    this.objSelectedDate.subtract(7, 'day');
    this.updateTable();
  }

  goToCurrentWeek(): void {
    this.objSelectedDate = moment.utc();
    this.updateTable();
  }

  goToNextWeek(): void {
    this.objSelectedDate.add(7, 'day');
    this.updateTable();
  }

  /**
   * Navigate to new pages.
   *
   * @param {CalendarPageDirection} strDirection
   *
   * @returns {void}
   */
  accessPage(strDirection: CalendarPageDirection): void {
    this.setPage(strDirection);
    this.tableUpdater.next();
  }

  getReadableTime(strDateTime: string): string {
    const datetime = moment.utc(strDateTime).local();
    return datetime.format('h:mm a');
  }

  momentDurationToString(duration: moment.Duration): string {
    const numHours = Math.floor(duration.asHours());
    const numMinutes = Math.floor(duration.asMinutes() - numHours * 60);

    let strText = `${numHours}h`;

    if (numMinutes > 0) {
      strText += ` ${numMinutes}m`;
    }

    return strText;
  }

  getTimesheetsList(strUserId: string, strModuleSearch: string = 'users'): Observable<{items: TimesheetInterface[], pagination: CalendarPagination}> {
    const strFilter: string = `filter[${strModuleSearch}]`;

    return this.http.post<{items: TimesheetInterface[], pagination: CalendarPagination}>(
      `${kBaseAPI}/timesheets/list`,
      new URLSearchParams({
        page: this.objPagination.current_page.toString(),
        // size: this.numResourcesSizePerPage.toString(),
        date_range: JSON.stringify(this.dateFilterLocalized),
        ...(strUserId && {
          [strFilter]: JSON.stringify([strUserId])
        }),
        size: this.getRecordPerPage().toString()
      })
    ).pipe(
      tap((result) => {
        this.objPagination = {
          ...this.objPagination,
          ...result.pagination
        };
      }),
    );
  }

  getTimesheetData(strTimesheetId: string, strUserId: string): Observable<{data: TimesheetInterface}> {
    return this.http.post<{data: TimesheetInterface}>(
      `${kBaseAPI}/timesheets/data`,
      new URLSearchParams({
        timesheet_id: strTimesheetId,
        user_id: strUserId,
        ...(this.objSelectedDate && {
          date_range: JSON.stringify(this.dateFilterLocalized),
        })
      })
    );
  }

  exportTimesheets(arTimesheetIds: string[], bSelectAll: boolean = false): Observable<HttpResponse<Blob>> {
    return this.http.post<Blob>(
      `${kBaseAPI}/timesheets/export`,
      new URLSearchParams({
        timesheet_ids: JSON.stringify(arTimesheetIds),
        date_range: JSON.stringify(this.dateFilterLocalized),
        select_all_within_date_range: bSelectAll ? '1' : '0'
      }),
      { headers: new HttpHeaders({ Accept: 'text/csv' }), responseType: 'blob' as 'json', observe: 'response' }
    )
  }

  massApproveTimesheets(arTimesheetIds: string[], bSelectAll: boolean = false) {
    return this.http.post<Blob>(
      `${kBaseAPI}/timesheets/mass_approve`,
      new URLSearchParams({
        timesheet_ids: JSON.stringify(arTimesheetIds),
        date_range: JSON.stringify(this.dateFilterLocalized),
        select_all_within_date_range: bSelectAll ? '1' : '0'
      }),
    );
  }

  exportTimesheetsData(arTimesheetIds: string[], bSelectAll: boolean = false): void {
    this.exportTimesheets(arTimesheetIds, bSelectAll).subscribe(
      response => {
        const strFilename = this.browserService.getFilenameFromResponseHeaders(response)

        this.browserService.forceDownload(
          response.body,
          strFilename.replace(/"/g, "")
        );

        this.bDisableButtons = false;
      },
      err => this.bDisableButtons = false
    )
  }

  getRecordPerPage(): number {
    let currentClient = this.localStorageService.getJsonItem('current_client');
    return currentClient.config.listing_records_per_page || 25;
  }
}
